<template>
	<div v-if="cell" :data-test="`table-cell--${column.key}`">
		<span v-if="cell.type === columnTypes.selectRow">
			<v-checkbox-btn
				v-if="column.key === calculatedColumnNames.select"
				v-model="isCheckboxSelected"
				density="compact"
			></v-checkbox-btn>
			<span v-else> - NOT IMPLEMENTED </span>
		</span>
		<span v-else-if="cell.type === columnTypes.badge && cell.value !== null">
			<v-chip
				:color="typeof cell.value === 'object' ? cell.value.color : cell.value"
				size="small"
				><span> {{ cell.value.text ?? cell.value }}</span></v-chip
			>
		</span>
		<span
			v-if="
				cell.type === columnTypes.text || cell.type === columnTypes.currency
			"
		>
			{{ cell.value }} </span
		><span
			v-else-if="cell.type === columnTypes.html"
			v-html="cell.value"
		></span>
		<span v-else-if="cell.type === columnTypes.tosAndPolicy">
			<v-tooltip location="bottom">
				<template #activator="{ props }">
					<v-icon color="red" v-bind="props">{{ cell.value }}</v-icon>
				</template>
				<span
					>Dieser Nutzer hat die Nutzungsbedingungen und Datenschutzbestimmungen
					nicht akzeptiert.</span
				>
			</v-tooltip>
		</span>
		<v-icon
			v-else-if="cell.type === columnTypes.icon"
			:color="cell.color"
			size="small"
			>{{ cell.value }}
		</v-icon>

		<DialogPreview
			v-else-if="cell.type === columnTypes.iconDocumentContainerDialog"
			:container-id="cell.id"
			:handle-prev-route="false"
			:dialog="false"
		>
			<template #activator="{ props }">
				<v-icon v-bind="props" :icon="cell.value"></v-icon>
			</template>
		</DialogPreview>

		<div v-else-if="cell.type === columnTypes.iconText">
			<v-icon class="pr-1" color="primary" size="small"
				>{{ cell.value.icon }}
			</v-icon>
			<span v-if="$vuetify.display.smAndUp">{{
				te(cell.value.text) ? translate(cell.value.text) : cell.value.text
			}}</span>
		</div>

		<read-more
			v-else-if="cell.type === columnTypes.readMore"
			:max-chars="
				$vuetify.display.lgAndUp ? 150 : $vuetify.display.md ? 100 : 50
			"
			:text="cell.value"
			more-str=""
		>
			<template #default="{ formattedString, text }">
				<v-tooltip location="bottom">
					<template #activator="{ props }">
						<span
							class="mt-2 d-block"
							v-bind="props"
							v-html="formattedString"
						></span>
					</template>
					<span v-html="text"></span>
				</v-tooltip>
			</template>
		</read-more>
		<v-avatar
			v-else-if="cell.type === columnTypes.avatar"
			size="36px"
			color="surface-darken-2"
		>
			<BaseImage
				v-if="cell.image === true"
				:src="cell.value"
				:error-icon="cell?.errorIcon"
				:error-icon-size="null"
			>
			</BaseImage>
			<v-icon v-else :icon="cell.value"> </v-icon>
		</v-avatar>
		<span v-else-if="cell.type === columnTypes.expand">
			<v-chip
				v-if="definition.multiple?.linkItem"
				@click="handleExpand(!isExpandedRow)"
				:disabled="row[definition.multiple.linkItem]?.length <= 0"
			>
				<v-icon size="x-small" class="px-2" start>
					{{ definition.multiple.icon }}
				</v-icon>

				{{ row[definition.multiple.linkItem]?.length }}
				<v-icon end size="x-small" class="pr-2">
					{{ isExpandedRow ? 'fas fa-chevron-up' : 'fas fa-chevron-down' }}
				</v-icon>
			</v-chip>
			<ButtonIcon
				v-else
				:icon="
					!isExpandedRow ? 'fa-solid fa-chevron-down' : 'fa-solid fa-chevron-up'
				"
				notext
				@click="handleExpand(!isExpandedRow)"
			/>
		</span>
	</div>
</template>

<script setup lang="ts">
import { useIsDark } from '@/composables/isDark'
import columnTypes from '@/constants/columnTypes'
import { computed, onMounted, ref } from 'vue'
import { useTheme } from 'vuetify'
import type {
	DataTableCell,
	DataTableRow,
	DataTableHeader,
	DataTableDefinition,
} from '@/types/table.ts'
import calculatedColumnNames from 'constants/calculatedColumnNames.ts'
import DialogPreview from '@/components/modules/Documents/DialogPreview.vue'
import { useTranslate } from 'composables/useTranslate.ts'
import BaseImage from '../BaseImage.vue'

const { column, row, definition, isSelected, isExpanded } = defineProps({
	row: {
		required: true,
		type: Object as () => DataTableRow,
	},
	column: {
		required: true,
		type: Object as () => DataTableHeader,
	},
	definition: {
		default: undefined,
		type: Object as () => DataTableDefinition,
		required: true,
	},
	expand: {
		default: () => {
			return {
				icon: undefined,
				is: false,
				items: [],
				name: undefined,
			}
		},
		required: false,
		type: Object,
	},
	isSelected: {
		type: Boolean,
		default: false,
	},
	isExpanded: {
		type: Boolean,
		default: false,
	},
})
const { translate, te } = useTranslate()
const emit = defineEmits(['expand', 'selected'])
const isDark = ref(false)
const { lightOrDark } = useIsDark()
const cell = ref<DataTableCell | undefined>(undefined)

const isCheckboxSelected = computed({
	get: () => isSelected,
	set: (value) => emit('selected', value),
})

const isExpandedRow = computed({
	get: () => isExpanded,
	set: (value) => emit('expand', value),
})

onMounted(async () => {
	isDark.value = lightOrDark(useTheme().current.value.colors.primary)
	cell.value = await getColumnValue(column, row)
})

function handleExpand(expanded: boolean) {
	isExpandedRow.value = expanded
}

function colorTheText(value: string) {
	if (!lightOrDark(value)) {
		return '#ffffff'
	} else {
		return '#000000'
	}
}

async function getColumnValue(
	header: DataTableHeader,
	item: DataTableRow
): Promise<{ column: DataTableCell }> {
	let extractedValue
	let linkItem = undefined

	if (
		header.hasMultiple &&
		definition?.multiple?.linkItem &&
		item[definition.multiple?.linkItem]
	) {
		linkItem = item[definition.multiple.linkItem][0]
	}

	if (header.pickKey) {
		if (Array.isArray(header.pickKey)) {
			extractedValue = header.pickKey
				.map((key) => getNestedValue(linkItem ?? item, key))
				.filter((val) => val != null && val !== '')
				.join(header.keyJoin ? header.keyJoin : ' ')
		} else {
			extractedValue = getNestedValue(linkItem ?? item, header.pickKey)
		}
	} else {
		extractedValue = getNestedValue(linkItem ?? item, header.key)
	}

	if (!extractedValue && header.ifNull) {
		if (Array.isArray(header.ifNull)) {
			extractedValue = header.ifNull
				.map((key) => getNestedValue(linkItem ?? item, key))
				.filter((val) => val != null && val !== '')
				.join(header.keyJoin ? header.keyJoin : ' ')
		} else {
			extractedValue = getNestedValue(linkItem ?? item, header.ifNull)
			extractedValue ??= header.ifNull
		}
	}

	if (header.format) {
		let formatted = header.format(extractedValue)

		if (formatted instanceof Promise) {
			formatted = await formatted
		}
		return Promise.resolve(formatted)
	}

	if (header.formatWithItem) {
		const formatted = await header.formatWithItem(extractedValue, item)
		return Promise.resolve(formatted)
	}

	if (header.type === columnTypes.selectRow) {
		return Promise.resolve(header)
	}
	if (header.type === columnTypes.expand) {
		return Promise.resolve(header)
	}
	return Promise.resolve({
		type: columnTypes.text,
		value: extractedValue,
	})
}

function getNestedValue(obj: any, path: string): any {
	return path.split('.').reduce((current, key) => {
		if (!current) return undefined

		// Match keys like persons[0]
		const match = key.match(/^(\w+)\[(\d+)\]$/)
		if (match) {
			const [, prop, index] = match
			return current[prop]?.[Number(index)]
		}

		return current[key]
	}, obj)
}
</script>

<style scoped></style>
